import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Info,
  Person,
  PlayArrow,
  HistoryEdu,
  EventNote,
  Send,
  Replay,
  AccessTime,
  DeleteOutline,
  ContentCopy,
  CallSplit,
  AssignmentTurnedIn,
  Forum,
  TaskAlt,
  Tune,
  Outbox,
} from "@material-ui/icons";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { formatearFecha } from "src/utils/formatTime";
import ModalExtenderPropuesta from "src/components/Modal/propuestas/Modal-extender-propuesta";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import BloquePanelAdmin from "./BloquePanelAdmin";
import TrackingTimeline from "./TrackingTimeline";
import ModalAceptacionInternaV2 from "./ModalAceptacionInternaV2";
import ModalObservacionInternaV2 from "./ModalObservacionInternaV2";
import ModalCierreExplicitoV2 from "./ModalCierreExplicitoV2";
import ModalAjusteServiciosV2 from "./ModalAjusteServiciosV2";
import ModalEnvioRondaV2 from "./ModalEnvioRondaV2";
import { BRAND } from "src/theme/brandTokens";

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return null;
  const limpia = String(fechaStr).replace(/\//g, "-");
  if (/^\d{4}-\d{2}-\d{2}$/.test(limpia)) return limpia;
  if (/^\d{4}-\d{2}$/.test(limpia)) return `${limpia}-01`;
  return null;
};

const formatFechaCompleta = (fecha) => {
  const n = normalizarFecha(fecha);
  if (!n) return "—";
  return formatearFecha(n, "DD [de] MMMM [de] YYYY");
};

const formatPeriodo = (fecha) => {
  const n = normalizarFecha(fecha);
  if (!n) return "—";
  return formatearFecha(n, "MMMM [de] YYYY");
};

const FilaMeta = ({ label, value, caption, children }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    sx={{ py: 0.5 }}
    spacing={1}
  >
    <Typography
      variant="caption"
      color="textSecondary"
      sx={{ flexShrink: 0, pt: 0.25 }}
    >
      {label}
    </Typography>
    {children ?? (
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value ?? "—"}
        </Typography>
        {caption && (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block" }}
          >
            {caption}
          </Typography>
        )}
      </Box>
    )}
  </Stack>
);

FilaMeta.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  caption: PropTypes.node,
  children: PropTypes.node,
};

FilaMeta.defaultProps = {
  value: undefined,
  caption: undefined,
  children: undefined,
};

/**
 * Panel administrativo (columna derecha) de VerPropuestaV2.
 * 5 bloques: Metadata · Solicitante · Acciones · Notas internas · Historial.
 * Sticky en desktop; sin sticky en mobile (layout lineal).
 *
 * El state del modal de envío vive en el parent (VerPropuestaV2) para que
 * el badge del header y este panel compartan el mismo flujo de apertura.
 * Los handlers se reciben como props: onAbrirEnvio y onAbrirReenvio.
 */
const PanelAdmin = ({ propuesta, refetch, onAbrirEnvio, onAbrirReenvio }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // ── Estado de modales
  const [openModalExtender, setOpenModalExtender] = useState(false);
  const [tipoAccionModal, setTipoAccionModal] = useState("extender");
  const [openAceptacionInterna, setOpenAceptacionInterna] = useState(false);
  const [openObservacionInterna, setOpenObservacionInterna] = useState(false);
  const [openCierreExplicito, setOpenCierreExplicito] = useState(false);
  const [openAjusteServicios, setOpenAjusteServicios] = useState(false);
  const [openEnvioRonda, setOpenEnvioRonda] = useState(false);
  // Acumula lo que ya devolvió cada guardado de ajuste mientras el panel
  // sigue montado, para que el diálogo de envío de la ronda pueda mostrar un
  // resumen sin depender de un endpoint de previsualización que no existe.
  // No sobrevive a un recargo de página a propósito: mostrar un conteo viejo
  // como si fuera el de la ronda que se está por enviar sería peor que no
  // mostrar nada.
  const [resumenAjuste, setResumenAjuste] = useState(null);

  const acumularResumenAjuste = (resultado) => {
    setResumenAjuste((prev) => ({
      cambiosRegistrados:
        (prev?.cambiosRegistrados ?? 0) + (resultado?.cambiosRegistrados ?? 0),
      cantidadRetirados:
        (prev?.cantidadRetirados ?? 0) + (resultado?.cantidadRetirados ?? 0),
      cantidadAgregados:
        (prev?.cantidadAgregados ?? 0) + (resultado?.cantidadAgregados ?? 0),
    }));
  };

  // ── Mutations
  const [eliminarPropuesta, { isLoading: eliminando }] =
    propuestasApi.useDeletePropuestaMutation();
  const [duplicarPropuesta, { isLoading: duplicando }] =
    propuestasApi.useDuplicarPropuestaMutation();
  const [crearDesdeRechazados, { isLoading: creandoDesdeRechazados }] =
    propuestasApi.useCrearPropuestaDesdeRechazadosMutation();

  // ── Derivados de estado
  const estado = propuesta.estado;
  const puedeEditar = estado === "BORRADOR";
  const puedeEnviar = estado === "BORRADOR";
  const puedeReenviar = estado === "PENDIENTE";
  const puedeAceptarInterna = estado === "PENDIENTE";
  const puedeExtender = estado === "PENDIENTE";
  const puedeReactivar = ["CADUCADA", "RECHAZADA"].includes(estado);
  const puedeEliminar = estado === "BORRADOR";
  const puedeDuplicar = [
    "BORRADOR",
    "ACEPTADA",
    "RECHAZADA",
    "CADUCADA",
  ].includes(estado);
  const puedeCrearDesdeRechazados = estado === "PARCIALMENTE_ACEPTADA";
  // Registrar lo que el cliente pidió por fuera abre la conversación, y también
  // sirve durante ella: por teléfono puede aceptar un servicio que quedó
  // pendiente mientras se negocia otro.
  const puedeRegistrarObservacion = ["PENDIENTE", "EN_RENEGOCIACION"].includes(
    estado,
  );
  // Ajustar servicios y enviar la ronda siguiente son las dos acciones que
  // mueven una conversación hacia la ronda que sigue; solo tienen sentido
  // mientras la propuesta sigue efectivamente en negociación.
  const puedeAjustarServicios = estado === "EN_RENEGOCIACION";
  const puedeEnviarRonda = estado === "EN_RENEGOCIACION";
  const puedeCerrar = estado === "EN_RENEGOCIACION";

  // Editar entra al agregador aunque hoy comparta condicion con enviar: este
  // agregador decide si se dibuja el bloque donde vive su boton, asi que el dia
  // que las dos condiciones dejen de coincidir el boton desapareceria sin que
  // nada parezca roto.
  const hayAccionesVisibles =
    puedeEditar ||
    puedeEnviar ||
    puedeReenviar ||
    puedeAceptarInterna ||
    puedeExtender ||
    puedeReactivar ||
    puedeEliminar ||
    puedeDuplicar ||
    puedeCrearDesdeRechazados ||
    puedeRegistrarObservacion ||
    puedeAjustarServicios ||
    puedeEnviarRonda ||
    puedeCerrar;

  // ── Handlers
  const handleEnviar = () => {
    onAbrirEnvio?.();
  };

  const handleReenviar = () => {
    onAbrirReenvio?.();
  };

  const handleExtender = () => {
    setTipoAccionModal("extender");
    setOpenModalExtender(true);
  };

  const handleReactivar = () => {
    setTipoAccionModal("reactivar");
    setOpenModalExtender(true);
  };

  const handleEliminar = async () => {
    const result = await AlertaModalV2({
      title: "¿Eliminar propuesta?",
      text: "Se eliminará la propuesta en estado BORRADOR. Esta acción no se puede deshacer.",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    }).fire();
    if (!result.isConfirmed) return;
    try {
      await eliminarPropuesta(propuesta.id).unwrap();
      enqueueSnackbar("Propuesta eliminada", { variant: "success" });
      navigate("/dashboard/propuestas");
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al eliminar la propuesta",
        { variant: "error" },
      );
    }
  };

  const handleDuplicar = async () => {
    try {
      const resultado = await duplicarPropuesta({
        id: propuesta.id,
      }).unwrap();
      const nuevoId = resultado?.idPropuesta ?? resultado?.id;
      enqueueSnackbar("Propuesta duplicada", { variant: "success" });
      if (nuevoId) {
        navigate(`/dashboard/propuestas/v2/${nuevoId}/editar`);
      } else {
        refetch?.();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al duplicar la propuesta",
        { variant: "error" },
      );
    }
  };

  const handleCrearDesdeRechazados = async () => {
    try {
      const resultado = await crearDesdeRechazados({
        id: propuesta.id,
      }).unwrap();
      const nuevoId = resultado?.idPropuesta ?? resultado?.id;
      enqueueSnackbar("Propuesta creada desde ítems rechazados", {
        variant: "success",
      });
      if (nuevoId) {
        navigate(`/dashboard/propuestas/v2/${nuevoId}/editar`);
      } else {
        refetch?.();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al crear la propuesta desde rechazados",
        { variant: "error" },
      );
    }
  };

  // ── Render
  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2,
          position: { md: "sticky" },
          top: 16,
          maxHeight: { md: "calc(100vh - 32px)" },
          overflowY: { md: "auto" },
        }}
      >
        <Stack spacing={3}>
          {/* ── Metadata ──────────────────────────── */}
          <BloquePanelAdmin
            titulo="Información general"
            icono={<Info fontSize="small" color="action" />}
          >
            <Stack spacing={0}>
              <FilaMeta label="Sucursal" value={propuesta.sucursal || "—"} />
              <FilaMeta
                label="Vendedor"
                value={propuesta.vendedorNombre || propuesta.vendedor || "—"}
                caption={propuesta.vendedorNombre ? propuesta.vendedor : null}
              />
              {propuesta.supervisor && (
                <FilaMeta
                  label="Supervisor"
                  value={propuesta.supervisorNombre || propuesta.supervisor}
                  caption={
                    propuesta.supervisorNombre ? propuesta.supervisor : null
                  }
                />
              )}
              <FilaMeta
                label="Fecha de creación"
                value={formatFechaCompleta(propuesta.fechaCreacion)}
              />
              <FilaMeta
                label="Período"
                value={formatPeriodo(propuesta.fecha)}
              />
              {propuesta.fechaEnvioCorreo && (
                <FilaMeta
                  label="Enviada"
                  value={formatFechaCompleta(propuesta.fechaEnvioCorreo)}
                />
              )}
              {propuesta.refPropuestaOrigen && (
                <FilaMeta label="Origen">
                  <Box sx={{ textAlign: "right", minWidth: 0 }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() =>
                        navigate(
                          `/dashboard/propuestas/v2/${propuesta.refPropuestaOrigen}`,
                        )
                      }
                      sx={{ fontWeight: 500 }}
                    >
                      Propuesta #{propuesta.refPropuestaOrigen}
                    </Link>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block" }}
                    >
                      {propuesta.refTipoOrigen === "DESDE_RECHAZADOS"
                        ? "Creada desde ítems rechazados"
                        : "Duplicada"}
                    </Typography>
                  </Box>
                </FilaMeta>
              )}
            </Stack>
          </BloquePanelAdmin>

          {/* ── Solicitante ──────────────────────── */}
          <BloquePanelAdmin
            titulo="Solicitante"
            icono={<Person fontSize="small" color="action" />}
          >
            {propuesta.solicitante ? (
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {propuesta.solicitante.nombre}
                </Typography>
                {propuesta.solicitante.correo && (
                  <Typography variant="caption" color="textSecondary">
                    {propuesta.solicitante.correo}
                  </Typography>
                )}
                {propuesta.solicitante.telefono && (
                  <Typography variant="caption" color="textSecondary">
                    {propuesta.solicitante.telefono}
                  </Typography>
                )}
                {propuesta.solicitante.rut ? (
                  <Typography variant="caption" color="textSecondary">
                    RUT: {propuesta.solicitante.rut}
                  </Typography>
                ) : (
                  <Chip
                    label="Sin RUT"
                    size="small"
                    sx={{
                      mt: 0.5,
                      alignSelf: "flex-start",
                      bgcolor: BRAND.color.primary,
                      color: "#fff",
                    }}
                  />
                )}
              </Stack>
            ) : (
              <Typography variant="caption" color="textSecondary">
                Sin datos de solicitante.
              </Typography>
            )}
          </BloquePanelAdmin>

          {/* ── Acciones ─────────────────────────── */}
          {hayAccionesVisibles && (
            <BloquePanelAdmin
              titulo="Acciones"
              icono={<PlayArrow fontSize="small" color="action" />}
            >
              <Stack spacing={1}>
                {puedeEditar && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() =>
                      navigate(
                        `/dashboard/propuestas/v2/${propuesta.id}/editar`,
                      )
                    }
                    sx={{
                      borderColor: BRAND.color.primary,
                      color: BRAND.color.primary,
                    }}
                  >
                    Editar
                  </Button>
                )}
                {puedeEnviar && (
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    startIcon={<Send fontSize="small" />}
                    onClick={handleEnviar}
                    sx={{
                      bgcolor: BRAND.color.primary,
                      "&:hover": { bgcolor: BRAND.color.primaryDark },
                    }}
                  >
                    Enviar al cliente
                  </Button>
                )}
                {puedeReenviar && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<Send fontSize="small" />}
                    onClick={handleReenviar}
                  >
                    Reenviar al cliente
                  </Button>
                )}
                {puedeAceptarInterna && (
                  <Tooltip title="Registra la respuesta del cliente por servicio y activa de inmediato los que queden aceptados: es una decisión final, no abre conversación.">
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<AssignmentTurnedIn fontSize="small" />}
                      onClick={() => setOpenAceptacionInterna(true)}
                      color="success"
                    >
                      Aceptar y activar servicios
                    </Button>
                  </Tooltip>
                )}
                {puedeRegistrarObservacion && (
                  <Tooltip title="Registra lo que el cliente pidió ajustar. No activa nada por sí sola: si dejas un servicio observado, la propuesta pasa a negociación.">
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<Forum fontSize="small" />}
                      onClick={() => setOpenObservacionInterna(true)}
                    >
                      Registrar y abrir conversación
                    </Button>
                  </Tooltip>
                )}
                {puedeAjustarServicios && (
                  <Tooltip title="Edita precio o alcance de los servicios en conversación, o retíralos de la propuesta vigente. No envía nada al cliente todavía.">
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={<Tune fontSize="small" />}
                      onClick={() => setOpenAjusteServicios(true)}
                    >
                      Ajustar servicios
                    </Button>
                  </Tooltip>
                )}
                {puedeEnviarRonda && (
                  <Tooltip title="Renueva el enlace y le manda al cliente la propuesta con los servicios vigentes de esta ronda.">
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      startIcon={<Outbox fontSize="small" />}
                      onClick={() => setOpenEnvioRonda(true)}
                      sx={{
                        bgcolor: BRAND.color.primary,
                        "&:hover": { bgcolor: BRAND.color.primaryDark },
                      }}
                    >
                      Enviar ronda siguiente
                    </Button>
                  </Tooltip>
                )}
                {puedeExtender && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<AccessTime fontSize="small" />}
                    onClick={handleExtender}
                  >
                    Extender vigencia
                  </Button>
                )}
                {puedeCerrar && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    color="warning"
                    startIcon={<TaskAlt fontSize="small" />}
                    onClick={() => setOpenCierreExplicito(true)}
                  >
                    Cerrar propuesta
                  </Button>
                )}
                {puedeReactivar && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<Replay fontSize="small" />}
                    onClick={handleReactivar}
                  >
                    Reactivar propuesta
                  </Button>
                )}
                {puedeCrearDesdeRechazados && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={
                      creandoDesdeRechazados ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CallSplit fontSize="small" />
                      )
                    }
                    onClick={handleCrearDesdeRechazados}
                    disabled={creandoDesdeRechazados}
                    sx={{
                      borderColor: BRAND.color.primary,
                      color: BRAND.color.primary,
                    }}
                  >
                    {creandoDesdeRechazados
                      ? "Creando..."
                      : "Crear desde rechazados"}
                  </Button>
                )}
                {puedeDuplicar && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={
                      duplicando ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )
                    }
                    disabled={duplicando}
                    onClick={handleDuplicar}
                  >
                    {duplicando ? "Duplicando…" : "Duplicar"}
                  </Button>
                )}
                {puedeEliminar && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    color="error"
                    startIcon={
                      eliminando ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <DeleteOutline fontSize="small" />
                      )
                    }
                    disabled={eliminando}
                    onClick={handleEliminar}
                  >
                    {eliminando ? "Eliminando…" : "Eliminar"}
                  </Button>
                )}
              </Stack>
            </BloquePanelAdmin>
          )}

          {/* ── Notas internas ───────────────────── */}
          {propuesta.observaciones && (
            <BloquePanelAdmin
              titulo="Notas internas"
              icono={<HistoryEdu fontSize="small" color="action" />}
            >
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  borderLeft: `3px solid ${BRAND.color.primary}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
                >
                  {propuesta.observaciones}
                </Typography>
              </Box>
            </BloquePanelAdmin>
          )}

          {/* ── Historial ────────────────────────── */}
          <BloquePanelAdmin
            titulo="Historial de eventos"
            icono={<EventNote fontSize="small" color="action" />}
          >
            <TrackingTimeline propuestaId={propuesta.id} />
          </BloquePanelAdmin>
        </Stack>
      </Paper>

      {/* ── Modales ──────────────────────────────── */}
      {openModalExtender && (
        <ModalExtenderPropuesta
          open={openModalExtender}
          setOpen={setOpenModalExtender}
          propuesta={propuesta}
          tipo={tipoAccionModal}
          onSuccess={refetch}
        />
      )}
      {openAceptacionInterna && (
        <ModalAceptacionInternaV2
          open={openAceptacionInterna}
          setOpen={setOpenAceptacionInterna}
          propuesta={propuesta}
          onSuccess={refetch}
        />
      )}
      {openObservacionInterna && (
        <ModalObservacionInternaV2
          open={openObservacionInterna}
          setOpen={setOpenObservacionInterna}
          propuesta={propuesta}
          onSuccess={refetch}
        />
      )}
      {openCierreExplicito && (
        <ModalCierreExplicitoV2
          open={openCierreExplicito}
          setOpen={setOpenCierreExplicito}
          propuesta={propuesta}
          onSuccess={refetch}
        />
      )}
      {openAjusteServicios && (
        <ModalAjusteServiciosV2
          open={openAjusteServicios}
          setOpen={setOpenAjusteServicios}
          propuesta={propuesta}
          onSuccess={refetch}
          onAjusteGuardado={acumularResumenAjuste}
        />
      )}
      {openEnvioRonda && (
        <ModalEnvioRondaV2
          open={openEnvioRonda}
          setOpen={setOpenEnvioRonda}
          propuesta={propuesta}
          resumenAjuste={resumenAjuste}
          onSuccess={refetch}
          onEnviado={() => setResumenAjuste(null)}
        />
      )}
    </>
  );
};

PanelAdmin.propTypes = {
  propuesta: PropTypes.object.isRequired,
  refetch: PropTypes.func,
  onAbrirEnvio: PropTypes.func,
  onAbrirReenvio: PropTypes.func,
};

PanelAdmin.defaultProps = {
  refetch: undefined,
  onAbrirEnvio: undefined,
  onAbrirReenvio: undefined,
};

export default PanelAdmin;
